import type { LendingMode, LenderYields, BalanceData, AprData, LoopPostTradeMetrics } from '../types'
import { noOpResult, buildLoopResult, positivePart } from '../utils'
import {
  getOrganicYieldClose,
  getRewardYieldClose,
  getIntrinsicYieldClose,
} from '../yield'

/**
 * Compute post-trade balance & APR metrics for closing a leveraged position.
 *
 * Withdraws `dollarIn` of collateral and repays `dollarOut` of debt.
 */
export function computeCloseTradeDeltas(
  dollarIn: number,
  dollarOut: number,
  targetMode: LendingMode,
  yieldParamsIn: LenderYields,
  yieldParamsOut: LenderYields,
  balance: BalanceData,
  apr: AprData,
  bfOut: number,
  ltvIn: number,
  collateralLtvIn: number
): LoopPostTradeMetrics {
  if (dollarIn === 0 || dollarOut === 0) return noOpResult(balance, apr)

  const cashFlowDeposits = balance.deposits * apr.depositApr
  const cashFlowDebt = balance.debt * apr.borrowApr
  const rewardFlowDeposits = balance.deposits * (apr.rewardDepositApr ?? 0)
  const rewardFlowDebt = balance.debt * (apr.rewardBorrowApr ?? 0)
  const intrinsicFlowDeposits = balance.deposits * apr.intrinsicDepositApr
  const intrinsicFlowDebt = balance.debt * apr.intrinsicBorrowApr

  const newBalance = { ...balance }
  const newApr = { ...apr }

  newBalance.debt = positivePart(newBalance.debt - dollarOut)
  newBalance.borrowDiscountedCollateral = positivePart(
    newBalance.borrowDiscountedCollateral - ltvIn * dollarIn
  )
  newBalance.collateral = positivePart(
    newBalance.collateral - collateralLtvIn * dollarIn
  )
  newBalance.deposits = positivePart(newBalance.deposits - dollarIn)
  newBalance.adjustedDebt = positivePart(
    newBalance.adjustedDebt - bfOut * dollarOut
  )
  newBalance.nav += dollarOut - dollarIn

  ;[newApr.borrowApr, newApr.depositApr, newApr.apr] = getOrganicYieldClose(
    dollarIn, dollarOut, targetMode,
    cashFlowDeposits, cashFlowDebt,
    balance.deposits, balance.debt,
    yieldParamsIn, yieldParamsOut
  )
  newApr.rewards = getRewardYieldClose(
    dollarIn, dollarOut, targetMode,
    rewardFlowDeposits, rewardFlowDebt,
    balance.deposits, balance.debt,
    yieldParamsIn, yieldParamsOut
  )
  ;[newApr.intrinsicBorrowApr, newApr.intrinsicDepositApr, newApr.intrinsicApr] =
    getIntrinsicYieldClose(
      dollarIn, dollarOut,
      intrinsicFlowDeposits, intrinsicFlowDebt,
      balance.deposits, balance.debt,
      yieldParamsIn, yieldParamsOut
    )

  return buildLoopResult(balance, newBalance, apr, newApr)
}
